import { type JsonPathLiteral } from "./JsonPathLiteral";
import { parseJsonPath } from "./parseJsonPath";

export function insertAtPath(record: unknown, path: JsonPathLiteral, value: unknown): unknown {
	const components = parseJsonPath(path);

	// Handle root level
	if (components.length === 0) {
		return value;
	}

	// Create a deep copy of the record to avoid mutating the input
	const result = JSON.parse(JSON.stringify(record));

	// Initialize first component if it's null
	const firstComponent = components[0];
	if (firstComponent && firstComponent.type === "object") {
		const key = firstComponent.key;
		if (result[key] === null) {
			result[key] = {};
		}
	}

	// Navigate to the parent of the target path
	let parent = result;
	for (let i = 0; i < components.length - 1; i++) {
		const component = components[i];
		if (!component) {
			continue;
		}

		if (component.type === "object") {
			// Convert null/undefined to empty object
			if (parent === null || parent === undefined) {
				parent = {};
			}
			if (!(component.key in parent)) {
				// Check if next component is an array to create appropriate type
				const nextComponent = components[i + 1];
				if (nextComponent && nextComponent.type === "array") {
					(parent as Record<string, unknown>)[component.key] = [];
				} else {
					(parent as Record<string, unknown>)[component.key] = {};
				}
			}
			parent = (parent as Record<string, unknown>)[component.key];
		} else if (component.type === "array") {
			// Convert null/undefined to empty array
			if (parent === null || parent === undefined) {
				parent = [];
			}
			if (!Array.isArray(parent)) {
				parent = [];
			}
			const arr = parent as unknown[];

			if (component.index === "append") {
				arr.push({});
				parent = arr[arr.length - 1];
			} else if (typeof component.index === "object") {
				const index = arr.length - component.index.fromEnd;
				if (index < 0 || index >= arr.length) {
					throw new Error(`Invalid array index: ${index}`);
				}
				parent = arr[index];
			} else {
				if (component.index >= arr.length) {
					arr.length = component.index + 1;
				}
				if (!arr[component.index]) {
					// Check if next component is an array to create appropriate type
					const nextComponent = components[i + 1];
					if (nextComponent && nextComponent.type === "array") {
						arr[component.index] = [];
					} else {
						arr[component.index] = {};
					}
				}
				parent = arr[component.index];
			}
		}
	}

	// Insert the value at the target path
	const lastComponent = components[components.length - 1];
	if (!lastComponent) {
		throw new Error("Invalid path: empty component");
	}

	if (lastComponent.type === "object") {
		// Convert null/undefined to empty object
		if (parent === null || parent === undefined) {
			parent = {};
		}
		(parent as Record<string, unknown>)[lastComponent.key] = value;
	} else if (lastComponent.type === "array") {
		// Convert null/undefined to empty array
		if (parent === null || parent === undefined) {
			parent = [];
		}
		const arr = parent as unknown[];
		if (!Array.isArray(arr)) {
			throw new Error("Cannot insert at array index in non-array");
		}

		if (lastComponent.index === "append") {
			arr.push(value);
		} else if (typeof lastComponent.index === "object") {
			const index = arr.length - lastComponent.index.fromEnd;
			if (index < 0 || index >= arr.length) {
				throw new Error(`Invalid array index: ${index}`);
			}
			arr[index] = value;
		} else {
			if (lastComponent.index >= arr.length) {
				arr.length = lastComponent.index + 1;
			}
			arr[lastComponent.index] = value;
		}
	}

	return result;
}
